import React from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";

interface Props {
	open: boolean;
	title: string;
	subtitle: string;
	fieldLabel: string;
	items: string[];
	maxItems: number;
	placeholder?: string;
	onSave: (items: string[]) => void;
	onCancel: () => void;
}

interface State {
	draft: string[];
	inputValue: string;
}

class Component extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			draft: props.items.filter(s => s.trim() !== ""),
			inputValue: "",
		};
	}

	public componentDidUpdate(prevProps: Props) {
		if (!prevProps.open && this.props.open) {
			this.setState({
				draft: this.props.items.filter(s => s.trim() !== ""),
				inputValue: "",
			});
		}
	}

	private handleAdd = () => {
		const { maxItems } = this.props;
		const { draft, inputValue } = this.state;
		const trimmed = inputValue.trim();
		if (trimmed && draft.length < maxItems) {
			this.setState({ draft: [...draft, trimmed], inputValue: "" });
		}
	};

	private handleSave = () => {
		const { maxItems, onSave } = this.props;
		const { draft } = this.state;
		const padded = Array.from({ length: maxItems }, (_, i) => draft[i] ?? "");
		onSave(padded);
	};

	public render() {
		const { open, title, subtitle, fieldLabel, placeholder, onCancel, maxItems } = this.props;
		const { draft, inputValue } = this.state;
		const atMax = draft.length >= maxItems;

		return (
			<Mui.Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
				<Mui.DialogTitle>
					<Mui.Box display="flex" alignItems="center" justifyContent="space-between">
						<Mui.Typography variant="h6" style={{ fontWeight: "bold", fontSize: 24 }}>{title}</Mui.Typography>
						<Mui.IconButton size="small" onClick={onCancel}><Icons.X size={24} /></Mui.IconButton>
					</Mui.Box>
				</Mui.DialogTitle>
				<Mui.Divider style={{ marginLeft: 24, marginRight: 24, backgroundColor: "#EBEBEB" }} />
				<Mui.DialogContent>
					<Mui.Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
						{subtitle}
					</Mui.Typography>
					<Mui.Box display="flex" alignItems="center" style={{ gap: 8 }}>
						<Mui.TextField
							fullWidth
							variant="outlined"
							size="small"
							label={fieldLabel}
							value={inputValue}
							onChange={(e) => this.setState({ inputValue: e.target.value })}
							onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); this.handleAdd(); } }}
							disabled={atMax}
							placeholder={placeholder}
						/>
						<Mui.Button
							color="primary"
							onClick={this.handleAdd}
							disabled={!inputValue.trim() || atMax}
							style={{ whiteSpace: "nowrap", minWidth: 64 }}
						>
							+ Add
						</Mui.Button>
					</Mui.Box>
					<Mui.Typography variant="body2" color="textSecondary" style={{ marginTop: 16, marginBottom: 8 }}>
						{draft.length} / {maxItems} Added
					</Mui.Typography>
					<Mui.Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
						{draft.map((item, i) => (
							<Mui.Chip
								key={i}
								label={item}
								size="small"
								style={{ backgroundColor: "#EBEBEB", color: "#1C1B1F" }}
								deleteIcon={<Icons.X size={14} style={{ color: "#1C1B1F" }} />}
								onDelete={() => this.setState(prev => ({
									draft: prev.draft.filter((_, idx) => idx !== i),
								}))}
							/>
						))}
					</Mui.Box>
				</Mui.DialogContent>
				<Mui.DialogActions style={{ padding: "16px 24px" }}>
					<Mui.Button variant="outlined" color="primary" onClick={onCancel}>Cancel</Mui.Button>
					<Mui.Button variant="contained" color="primary" onClick={this.handleSave}>Apply</Mui.Button>
				</Mui.DialogActions>
			</Mui.Dialog>
		);
	}
}

export const TextListModal = Component;
